//common
import { useState, useMemo, useRef, useEffect } from 'react';
import { useLoaderData } from "react-router-dom";

//deck.gl
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckGL, DeckGLRef } from '@deck.gl/react';
import { COORDINATE_SYSTEM, Deck, OrthographicView, OrthographicViewState, PickingInfo } from '@deck.gl/core';
import { MjolnirGestureEvent } from "mjolnir.js/"
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer, GeoJsonLayer, PolygonLayer } from '@deck.gl/layers';
import { FunnelFill, Download, Arrows, Clipboard } from "react-bootstrap-icons";

//satellite
import { Button } from "./components/Button";
import { DetectionLoaderData } from "./loaders/detectionLoader";
import { DetectFilter, DetectFilterModal } from "./components/DetectFilterModal";
import { ObbDet } from "./api/Application";
import { downloadCsv } from "./Csv";
import { DetectionListEntry } from "./DetectionListPageDetectionItem";
import { aerialImageTileSpec } from "./api/Url";
import { FaArrowsAlt } from "react-icons/fa";
import { createVehiclePolygon } from "./common/Geometry";

interface DetProps<T> { properties: T; };

export function DetectionListPage() {
    const initialValue: DetectFilter = {
        minConfThr:   0.0,
        maxConfThr:   1.0,
        widthThr:     0.0,
        heightThr:    0.0
    }
    //selection
    const [ rect, setRect ] = useState<number[]>();
    const [ selectionActive, setSelectionActive] = useState<boolean>( false );
    const [ selectedObbDets, setSelectedObbDets] = useState<ObbDet[]>( [] );
    const deckRef = useRef<DeckGLRef | null>(null);

    const [ filters,        setFilters        ] = useState<DetectFilter>( initialValue );
    const [ showFilters,    setShowFilters    ] = useState<boolean>( false );
    const { image, vehicles } = useLoaderData() as DetectionLoaderData;
    const width  = Math.ceil( image.width  / 512.0 ) * 512;
    const height = Math.ceil( image.height / 512.0 ) * 512;
    const initViewState: OrthographicViewState = {
        target: [image.height / 2, image.width / 2, 0],
        zoom:   0
    };

    useEffect( () => {
            const handleKeyDown = ( e: KeyboardEvent ) => {
                switch( e.key ) {
                    case "Escape": setSelectionActive( false ); break;
                    case "q":      setSelectionActive( false ); break;
                    case "v":      setSelectionActive( false ); break;
                    case "w":      setSelectionActive( true  ); break;
                }
            };
            window.addEventListener( "keydown", handleKeyDown );
            return () => window.removeEventListener( "keydown", handleKeyDown );
        }, [] )

    const tileLayer = new TileLayer<ImageBitmap>( {
        pickable:           true,
        tileSize:           512,
        refinementStrategy: "best-available",
        minZoom:            -3,
        maxZoom:            0,
        extent:             [0, 0, width, height],
        data:               aerialImageTileSpec( image.aerialImageId ),
        renderSubLayers: props => {
            const [[left, bottom], [right, top]] = props.tile.boundingBox;
            const { data, ...otherProps } = props;
            return new BitmapLayer( otherProps, {
                image: data,
                bounds: [
                    left,
                    top,
                    right,
                    bottom
                ],
                textureParameters: {
                    minFilter: 'nearest',
                    magFilter: 'nearest'
                }
            } );
        }
    } );

    const detections = useMemo( () => ( {
        type: "FeatureCollection" as const,
        features: vehicles
            .map( obj => {
                const vpnts = createVehiclePolygon( obj );                
                return {
                    type: "Feature" as const,
                    geometry: {
                        type: "Polygon" as const,
                        coordinates: [vpnts],
                    },
                    properties: obj
                };
            } )
    } ), [vehicles] );

    const geoJsonLayer = useMemo( () => {
        return new GeoJsonLayer( {
            id:                 "detections",
            autoHighlight:      true,
            highlightColor:     [0, 256, 0, 100],
            data:               detections,
            filled:             true,
            stroked:            true,
            lineJointRounded:   true,
            getFillColor:       [0, 0, 0, 0],
            getLineColor:       [0, 255, 0, 200],
            lineWidthMinPixels: 2,
            pickable:           true, 
            coordinateSystem:   COORDINATE_SYSTEM.CARTESIAN,                        
            extensions:         [new DataFilterExtension( { filterSize: 3 } )],            
            getFilterValue: ( f: DetProps<ObbDet> ) => {
                return [
                    f.properties.confidence,
                    f.properties.width,
                    f.properties.height
                ];
            },
            filterRange: [
                [filters.minConfThr, filters.maxConfThr     ],
                [filters.widthThr,   Number.MAX_SAFE_INTEGER],
                [filters.heightThr,  Number.MAX_SAFE_INTEGER],
            ],
            updateTriggers: {
                getFilterValue: [filters.minConfThr, filters.widthThr, filters.heightThr],
            },
            onClick: ( info, event ) => {
                if( !info.object ) {
                    return;
                }
                const e = event.srcEvent as MouseEvent;
                const payload = new Set<ObbDet>();
                if( e.shiftKey ) {
                    for( var obb of selectedObbDets ) {
                        payload.add( obb );
                    }
                }
                payload.add( info.object.properties as ObbDet );
                setSelectedObbDets( Array.from( payload ) );
            },
        } );
    }, [vehicles, filters, selectedObbDets] );

    const selectedDetections = useMemo( () => ( {
        type: "FeatureCollection" as const,
        features: selectedObbDets
            ?.map( obj => {
                const vpnts = createVehiclePolygon( obj );
                return {
                    type: "Feature" as const,
                    geometry: {
                        type: "Polygon" as const,
                        coordinates: [vpnts],
                    },
                    properties: obj
                };
            } ) ?? []
    } ), [selectedObbDets] );

    const selectedGeoJsonLayer = useMemo( () => {
        return new GeoJsonLayer( {
            id:                 "selectedDetections",
            data:               selectedDetections,
            filled:             true,
            stroked:            true,
            lineJointRounded:   true,
            getFillColor:       [   0, 255,   0, 128],
            getLineColor:       [   0, 255,   0, 200],
            lineWidthMinPixels: 2,
            pickable:           false, 
            coordinateSystem:   COORDINATE_SYSTEM.CARTESIAN,                        
            extensions:         [new DataFilterExtension( { filterSize: 3 } )],            
            getFilterValue: ( f: DetProps<ObbDet> ) => {
                return [
                    f.properties.confidence,
                    f.properties.width,
                    f.properties.height
                ];
            },
            filterRange: [
                [filters.minConfThr, filters.maxConfThr],
                [filters.widthThr,   Number.MAX_SAFE_INTEGER],
                [filters.heightThr,  Number.MAX_SAFE_INTEGER],
            ],
            updateTriggers: {
                getFilterValue: [filters.minConfThr, filters.widthThr, filters.heightThr],
            },
            onClick: ( info, event ) => {                
                if( info.object ) {
                    setSelectedObbDets( [info.object.properties as ObbDet] );
                    const obj = info.object.properties as ObbDet;
                    navigator.clipboard.writeText( `${obj.width}\t${obj.height}` );
                }
            },
        } );
    }, [selectedObbDets, filters] );

    const handleDownloadCsv = () => {
        const headers = ["ID", "X", "Y", "Length", "Width", "Confidence"];
        const rows = ( selectedObbDets ?? [] ).map( v => [
            v.obbDetId,
            v.x,
            v.y,
            v.width,
            v.height,
            v.confidence || "N/A",
        ] );
        downloadCsv( `AbandonedVehiclesInImage${image.aerialImageId}.csv`, headers, rows );
    };

    const handleClipboard = () => {
        const payload = selectedObbDets?.map( obj => `${obj.width}\t${obj.height}` ).join( "\r\n" )!;
        navigator.clipboard.writeText( payload );
    };

    const selectionLayer = useMemo( () => {
        if( !selectionActive           ) { return; }
        if( !rect || rect?.length == 0 ) { return; }
        const [x1, y1, x2, y2] = rect!;
        const viewport = deckRef.current?.deck?.getViewports()[0]!;
        const corners = [
            viewport.unproject( [x1, y1] ),
            viewport.unproject( [x2, y1] ),
            viewport.unproject( [x2, y2] ),
            viewport.unproject( [x1, y2] ),
        ];
        return new PolygonLayer( {
            id:                 'selection-box',
            data:               [{  polygon: corners }],
            getPolygon:         (   d:       any     ) => d.polygon,
            getFillColor:       [0, 255,     0,      40],
            getLineColor:       [0, 255,     0,      180],
            lineWidthMinPixels: 2,
            pickable:           false,
            coordinateSystem:   COORDINATE_SYSTEM.CARTESIAN,
        } );
    }, [rect] );

    function onDragStart( info: PickingInfo, e: MjolnirGestureEvent ) {        
        if( !selectionActive ) { return; }
        setRect( [e.offsetCenter.x, e.offsetCenter.y, e.offsetCenter.x, e.offsetCenter.y] );
    }

    function onDrag( info: PickingInfo, e: MjolnirGestureEvent ) {
        if( !selectionActive ) { return; }
        if( !rect            ) { return; }
        const [x1, y1, x2, y2] = rect;
        setRect( prev => ( [x1, y1, e.offsetCenter.x, e.offsetCenter.y ] ) );   
    }

    function onDragEnd( info: PickingInfo, e: MjolnirGestureEvent ) {
        if( !selectionActive || !rect ) { return; }
        let [x1, y1, x2, y2] = rect;
        [x1, y1, x2, y2] = [
            Math.min( x1, x2 ),
            Math.min( y1, y2 ),
            Math.max( x1, x2 ),
            Math.max( y1, y2 )
        ];

        const deck = deckRef.current?.deck;
        if( !deck ) {
            return
        };

        const pickFilter  = { x: x1, y: y1, width: x2 - x1, height: y2 - y1, layerIds: ['detections'] };
        const pickResults = deck.pickObjects( pickFilter );
        const selected = pickResults
            .map( p => p.object?.properties as ObbDet )
            .filter( Boolean );

        setSelectedObbDets( selected );
        setRect( undefined );
    }

    return <div className="h-100">
        <DetectFilterModal
            show={ showFilters }
            filter={ filters }
            setFilters={ setFilters }
            onHide={ () => setShowFilters( false ) } />

        <div id="capture-list-page">
            <div id="capture-list" style={{ display: "grid", gridTemplateRows: "auto auto 1fr" }}>
                <div className="bg-body-tertiary border d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2">
                    <div className="flex-grow-1 ps-2 fs-5">
                        Detection Results {vehicles.length}
                    </div>
                    <Button
                        override 
                        className={`px-2 py-1 rounded ${selectionActive ? "bg-body text-body" : "bg-primary text-white"}`} 
                        onClick={ () => setSelectionActive( !selectionActive )}>
                        <FaArrowsAlt  />
                    </Button>
                    <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={() => setShowFilters( true )}>
                        <FunnelFill />
                    </Button>
                    <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={handleDownloadCsv}>
                        <Download />                        
                    </Button>
                    <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={handleClipboard}>
                        <Clipboard />
                    </Button>
                </div>
                <div className="y-scroll-auto">
                    {selectedObbDets && <div>
                        {selectedObbDets.map( x => {
                            return <DetectionListEntry vehicle={x} />;
                        })}
                    </div>}
                </div>
            </div>
            <div className="image-viewer h-100 overflow-hidden position-relative" style={{ backgroundColor: "black" }}>
                <DeckGL
                    ref={deckRef}
                    views={ new OrthographicView() }
                    layers={[tileLayer, geoJsonLayer, selectionLayer, selectedGeoJsonLayer]}
                    initialViewState={initViewState as any}
                    controller={{                        
                        touchRotate: true,
                        dragPan:    !selectionActive,                        
                        scrollZoom: !selectionActive,
                        dragRotate: true,
                    }}
                    style={{ backgroundColor: "black" }}
                    getCursor={( { isDragging, isHovering } ) => {
                        if( isDragging ) {
                            return "grabbing";
                        }
                        if( isHovering ) {
                            return "pointer"; 
                        }
                        return "grab";
                    }}
                    onDragStart={ onDragStart }
                    onDrag={ onDrag }
                    onDragEnd={ onDragEnd }
                />
            </div>
        </div>
    </div>;
}